/**
 * Shared scaffolding for the two host IMA video renderers
 * (`IMAInterstitialVideo` and `IMARewardedVideo`).
 *
 * Both renderers need exactly the same overlay shape and adsManager
 * viewport wiring; before this helper, each had its own ~80 lines of
 * near-identical DOM and resize boilerplate. Keeping the IMA layout in
 * ONE place is the only way to make sure a future format (or a fix to
 * an existing one) stays in sync across both — the white-screen and
 * black-screen regressions earlier in this branch came from drift
 * between the two files.
 *
 * The renderer-specific pieces (rewarded's close X, the reward
 * disposition handling, the telemetry events) stay in the renderers —
 * this module is just the bits that MUST be identical.
 */
import { ensureImaIframeOverlayStyle, IMA_SURFACE_CLASS, } from './ima-iframe-overlay-style';
/**
 * Build the IMA overlay DOM with the standard layout used by every
 * host-rendered IMA video format. The caller mounts it
 * (`document.body.appendChild(containerElement)`) and may append
 * format-specific children (e.g. rewarded's close X) to the backdrop.
 *
 * The `idPrefix` parameterises the element IDs so DevTools inspection
 * and any host CSS overrides can target the format clearly
 * (`getjar-interstitial-*` vs `getjar-rewarded-*`).
 */
export function createImaOverlay(idPrefix) {
    ensureImaIframeOverlayStyle();
    const containerElement = document.createElement('div');
    containerElement.id = `getjar-${idPrefix}-container`;
    // Mobile-correct viewport sizing — see the matching comment in
    // gpt-interstitial.ts. `100vh` is the legacy "large" viewport height
    // on iOS Safari and includes the URL bar's reserved space; the bottom
    // of the overlay then sits off-screen when the URL bar is visible.
    // `100dvh` (dynamic) tracks the URL bar in real time. Declaring both
    // in order makes browsers without `dvh` support fall back to `vh`.
    //
    // The video is intentionally edge-to-edge here — IMA plays into the
    // full surface and we don't apply safe-area padding to the backdrop,
    // because the video should reach the screen edges. The renderer-side
    // chrome (rewarded's close X, confirm modal) is what carries safe-area
    // padding — see ima-rewarded-video.ts.
    containerElement.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    width: 100dvw;
    height: 100vh;
    height: 100dvh;
    background: #000;
    z-index: 999999;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
    const adContainerElement = document.createElement('div');
    adContainerElement.id = `getjar-${idPrefix}-ad-container`;
    adContainerElement.classList.add(IMA_SURFACE_CLASS);
    adContainerElement.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: transparent;
  `;
    // API-contract slot only — IMA plays the ad into its OWN <video>
    // inside its injected wrapper, NOT into this element. Inline styles
    // here are minimal because the `.getjar-ima-surface video` rule in
    // `ensureImaIframeOverlayStyle()` enforces the position/size with
    // `!important`. Critically, this element must NOT have `z-index`,
    // `background`, or any other style that would stack it over IMA's
    // playing video — earlier iterations had those and masked playback
    // (white/black with audio).
    const videoElement = document.createElement('video');
    videoElement.id = `getjar-${idPrefix}-video`;
    videoElement.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  `;
    videoElement.playsInline = true;
    adContainerElement.appendChild(videoElement);
    containerElement.appendChild(adContainerElement);
    return { containerElement, adContainerElement, videoElement };
}
/**
 * Wire IMA's adsManager to the viewport: init at `window.inner*`, then
 * a follow-up `resize` to lock the size in case init measurement runs
 * before the layout settles, plus a window `resize` / `orientationchange`
 * listener that keeps the iframe sized to the (possibly-new) viewport.
 *
 * Without this, IMA defaults to a fixed 1920×945 surface (the bug
 * symptom: house video plays mostly off-screen → black with audio).
 *
 * Also logs the ad container's measured dimensions immediately before
 * init — if those are 0×0, the bug is upstream (parent chain has
 * `height: 0`) and the video surface will never be visible. Catches
 * future regressions in one console line.
 *
 * Returns a teardown function that removes the listeners — the renderer
 * MUST call it from cleanup BEFORE destroying `adsManager`, otherwise
 * a late resize event can fire `adsManager.resize` on a destroyed
 * instance and throw.
 */
export function initImaAdsManagerToViewport(adsManager, ima, adContainer, logTag) {
    var _a, _b;
    const cw = (_a = adContainer === null || adContainer === void 0 ? void 0 : adContainer.clientWidth) !== null && _a !== void 0 ? _a : 0;
    const ch = (_b = adContainer === null || adContainer === void 0 ? void 0 : adContainer.clientHeight) !== null && _b !== void 0 ? _b : 0;
    console.log(`[GetJarAds IMA] ${logTag} init — adContainer=${cw}x${ch} viewport=${window.innerWidth}x${window.innerHeight}`);
    if (cw === 0 || ch === 0) {
        console.warn(`[GetJarAds IMA] ${logTag} ad container has zero dimension before adsManager.init — video surface will be invisible`);
    }
    adsManager.init(window.innerWidth, window.innerHeight, ima.ViewMode.FULLSCREEN);
    try {
        adsManager.resize(window.innerWidth, window.innerHeight, ima.ViewMode.FULLSCREEN);
    }
    catch (_c) {
        // resize() can throw if the ad isn't in a resizable state — same
        // best-effort guard as the listener below.
    }
    const handler = () => {
        try {
            adsManager === null || adsManager === void 0 ? void 0 : adsManager.resize(window.innerWidth, window.innerHeight, ima.ViewMode.FULLSCREEN);
        }
        catch (_a) {
            // Best-effort.
        }
    };
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    return () => {
        window.removeEventListener('resize', handler);
        window.removeEventListener('orientationchange', handler);
    };
}
